<template>
    <div class="el-field el-field-with-icon"
         :class="[
         { 'md-error': error },
         { 'md-focused': isFocused },
         { 'md-value-changed': hasValueChanged },
         { 'md-has-value': hasValue },
         { 'el-field-range': isRangeField }
     ]"
    >
        <label>
            <template v-if="!isRangeField">
                <label>{{ requiredPlaceholder }}</label>
            </template>
            <template v-else>
                <label class="label-range-start">{{ startPlaceholder }}</label>
                <label class="label-range-end">{{ endPlaceholder }}</label>
            </template>
        </label>
        <el-date-picker
            :type="type"
            v-model="value"
            :format="localizedDateFormat"
            :value-format="valueFormat"
            :placeholder="requiredPlaceholder"
            :start-placeholder="startPlaceholder"
            :end-placeholder="endPlaceholder"
            @change="onChange"
            @input="onInput"
            @focus="onFocus"
            @blur="onBlur"
            :disabled="disabled"
            :required="required"
            :unlink-panels="true"
            :picker-options="pickerOptions"
        ></el-date-picker>
        <span v-if="error" class="validation-error">{{ error }}</span>
    </div>
</template>

<script>
import {toPickerDateFormat} from "@/common/format";

export default {
    props: {
        type: { type: String, default: "date" },
        dateFormat: { type: String, default: "L" },
        valueFormat: { type: String, default: "yyyy-MM-dd" },
        placeholder: { type: String, default: "" },
        value: { type: String, default: "" },
        required: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        onlyFutureDates: { type: Boolean, default: false },
	    skipWeekends: { type: Boolean, default: false },
		minDate: { type: String, default: null },
		maxDate: { type: String, default: null },
        error: { type: String, default: "" }
    },
    data() {
        return {
            isFocused: false,
            hasValueChanged: false
        };
    },
	methods: {
        onChange() {
			// Triggers animations for value changes
			this.hasValueChanged = true;

			// Timeout has to be in sync with animation timing "$el-field-timings" defined in css
			setTimeout(() => {
				this.hasValueChanged = false;
			}, 300);
        },
        onInput() {
            this.value = this.adjustDate(this.value);
            this.$emit("input", this.value);
        },
        onFocus() {
            this.isFocused = true;
        },
        onBlur() {
            this.isFocused = false;
        },
        adjustDate(date) {
            return date;
        },
    },
    computed: {
        requiredPlaceholder() {
            return this.placeholder + (this.required && !this.isRangeField ? " *" : "")
        },
        startPlaceholder() {
            return this.$t("common.placeholder.range.start", { name: this.requiredPlaceholder }) + (this.required && this.isRangeField ? "*" : "");
        },
        endPlaceholder() {
            return this.$t("common.placeholder.range.end", { name: this.requiredPlaceholder }) + (this.required && this.isRangeField ? "*" : "");
        },
        pickerOptions() {
            let disableChecks = {
                onlyFutureDates: this.onlyFutureDates,
				skipWeekends: this.skipWeekends,
				minDate: this.minDate,
				maxDate: this.maxDate
            };

            return {
                disabledDate: (date) => {
					let dateToCheck = moment(moment(date).format("YYYY-MM-DD"));
                	let disabled = false;

					if (disableChecks.minDate) {
                		disabled |= dateToCheck < moment(disableChecks.minDate);
					}

					if (disableChecks.maxDate) {
						disabled |= dateToCheck > moment(disableChecks.maxDate);
					}

                	if (disableChecks.onlyFutureDates) {
                        disabled |= dateToCheck < moment().startOf('day');
                    }

                	if (disableChecks.skipWeekends) {
                		disabled |= [6, 7].includes(dateToCheck.isoWeekday());
					}

                    return disabled;
                },
                firstDayOfWeek: moment.localeData().firstDayOfWeek(),
            };
        },
        localizedDateFormat() {
            let localeFormatMatch = this.dateFormat.match(/L{1,4}/);

            // No format part needs to be localized, keep format as it is
            if (!localeFormatMatch) {
                return this.dateFormat;
            }

            // Replace locale specific format with language specific replacement
            let localeFormatPart = localeFormatMatch[0];
            let localizedMomentFormat = moment.localeData().longDateFormat(localeFormatPart);
            let localizedValueFormat = toPickerDateFormat(localizedMomentFormat);

            return this.dateFormat.replace(localeFormatPart, localizedValueFormat);
        },
        hasValue() {
            return this.value instanceof Array
                ? this.value.length > 0 && this.value.some(e => e)
                : this.value;
        },
        isRangeField() {
            return this.type.endsWith("range");
        }
    },
};
</script>

<style lang="scss" scoped>

.el-date-editor::v-deep {

    .el-range-separator {
        display: none;
    }
}

</style>